import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import {
  formatLanguage,
  formatTranslationStatus,
  getTranslationStatusColor,
} from '../utils/translationUi';

const STATUS_CHIP = {
  confirmed_scam: { label: 'Confirmed Scam', color: 'error' },
  high_risk:      { label: 'High Risk',       color: 'warning' },
  suspicious:     { label: 'Suspicious',      color: 'warning' },
  monitoring:     { label: 'Monitoring',      color: 'default' },
  cleared:        { label: 'Cleared',         color: 'success' },
};

function PatternMemoryCard({ callerId, victimId }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery(
    ['patternMemoryEntry', callerId, victimId],
    () => api.getPatternMemoryEntry(callerId, victimId),
    { retry: false }
  );

  if (isLoading) {
    return (
      <Box p={1.5} display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Loading pattern memory…</Typography>
      </Box>
    );
  }

  const statusCfg = data ? (STATUS_CHIP[data.status] || STATUS_CHIP.monitoring) : null;

  return (
    <Box px={2} py={1.5} bgcolor="action.hover">
      <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
        <ShieldIcon fontSize="small" color="action" />
        <Typography variant="caption" fontWeight="medium" color="text.secondary">
          Pattern Memory
        </Typography>
      </Stack>
      {error || !data ? (
        <Typography variant="caption" color="text.secondary">
          {error?.response?.status === 404 ? 'No pattern memory for this pair yet.' : 'Could not load pattern memory.'}
        </Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">Cumulative score</Typography>
            <Typography variant="body2" fontWeight="bold">{data.cumulativeScore} / 100</Typography>
          </Box>
          <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
          <Box>
            <Typography variant="caption" color="text.secondary">Calls</Typography>
            <Typography variant="body2">{data.callCount}</Typography>
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {(data.detectedSignals || []).map((s) => (
              <Chip key={s.signalId} label={s.signalId.replace(/_/g, ' ')} size="small" variant="outlined" />
            ))}
          </Stack>
          <Button
            size="small"
            variant="text"
            onClick={() => navigate('/risk-meter')}
          >
            View in Risk Meter
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export default function CallHistory() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [calleeSearch, setCalleeSearch] = useState('');
  const [filterSpammer, setFilterSpammer] = useState(false);
  const [filterFraudScore, setFilterFraudScore] = useState(false);
  const [filterRisky, setFilterRisky] = useState(false);
  const [filterRecentAbuse, setFilterRecentAbuse] = useState(false);

  const formatThailandTime = (timestamp) => {
    if (!timestamp) return '-';

    // If timestamp has no timezone, treat it as UTC to avoid double-localization.
    const timestampString = typeof timestamp === 'string' ? timestamp : new Date(timestamp).toISOString();
    const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(timestampString);
    const safeTimestamp = hasTimezone ? timestampString : `${timestampString}Z`;
    const date = new Date(safeTimestamp);

    return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    }).format(date);
  };

  const formatDurationMs = (value) => {
    const duration = Number(value);
    if (!Number.isFinite(duration) || duration < 0) return '-';
    const totalSeconds = Math.round(duration / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
    }
    return `${seconds}s`;
  };

  const { data, isLoading, isFetching, refetch } = useQuery(
    ['callHistory', page, rowsPerPage],
    () => api.getCallHistory({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      filterScammers: false,
    }),
    {
      keepPreviousData: true,
    }
  );

  const calls = data?.calls || [];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredCalls = calls.filter((call) => {
    const caller = call.phoneNumber || '';
    const callee = call.calleeNumber || '';
    const fraudScore = typeof call.fraudScore === 'number'
      ? call.fraudScore
      : parseFloat(call.fraudScore);

    if (search && !caller.includes(search)) return false;
    if (calleeSearch && !callee.includes(calleeSearch)) return false;
    if (filterSpammer && call.spammer !== true) return false;
    if (filterFraudScore && !(Number.isFinite(fraudScore) && fraudScore > 0)) return false;
    if (filterRisky && call.risky !== true) return false;
    if (filterRecentAbuse && call.recentAbuse !== true) return false;

    return true;
  });

  const normalizeAction = (action) => {
    if (!action) return '-';
    // Remove _EXECUTED suffix: ALLOW_EXECUTED -> ALLOW
    return action.replace(/_EXECUTED$/, '');
  };

  const getActionColor = (action) => {
    if (!action) return 'default';
    const normalized = normalizeAction(action);
    switch (normalized) {
      case 'BLOCK':
        return 'error';
      case 'DROP':
        return 'error';
      case 'WHISPER':
        return 'warning';
      case 'ALLOW':
      case 'LOG_ONLY':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleViewTrace = (callId) => {
    if (!callId) return;
    const url = `${window.location.origin}/sip-trace?traceId=${encodeURIComponent(callId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const navigate = useNavigate();
  const [expandedRow, setExpandedRow] = useState(null);
  const toggleExpand = (key) => setExpandedRow((prev) => (prev === key ? null : key));

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              placeholder="Search by caller number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              placeholder="Filter by callee number..."
              value={calleeSearch}
              onChange={(e) => setCalleeSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" flexWrap="wrap" gap={2}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={filterSpammer}
                    onChange={(e) => setFilterSpammer(e.target.checked)}
                  />
                )}
                label="Spammer"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={filterFraudScore}
                    onChange={(e) => setFilterFraudScore(e.target.checked)}
                  />
                )}
                label="Fraud score > 0"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={filterRisky}
                    onChange={(e) => setFilterRisky(e.target.checked)}
                  />
                )}
                label="Risky"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={filterRecentAbuse}
                    onChange={(e) => setFilterRecentAbuse(e.target.checked)}
                  />
                )}
                label="Recent abuse"
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Time</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Caller</TableCell>
                <TableCell>Callee</TableCell>
                <TableCell>Fraud Score</TableCell>
                <TableCell>Risky</TableCell>
                <TableCell>Recent Abuse</TableCell>
                <TableCell>Spammer</TableCell>
                <TableCell>Final Action</TableCell>
                <TableCell>Translation</TableCell>
                <TableCell align="center">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCalls.map((call, index) => {
                const rowKey = `${call.phoneNumber}-${call.calleeNumber}-${call.timestamp}`;
                const canExpand = Boolean(call.phoneNumber && call.calleeNumber && call.source === 'call');
                const isExpanded = expandedRow === rowKey;
                return (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        {canExpand ? (
                          <Tooltip title="Pattern memory">
                            <IconButton size="small" onClick={() => toggleExpand(rowKey)}>
                              {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {formatThailandTime(call.timestamp)}
                          {call.isTest && (
                            <Chip label="TEST" color="info" size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDurationMs(call.durationMs)}</TableCell>
                      <TableCell>{call.phoneNumber}</TableCell>
                      <TableCell>{call.calleeNumber || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={call.fraudScore !== null && call.fraudScore !== undefined ? call.fraudScore : '-'}
                          color={call.fraudScore >= 75 ? 'error' : call.fraudScore >= 50 ? 'warning' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {call.risky === true ? (
                          <Chip label="Yes" color="error" size="small" />
                        ) : call.risky === false ? (
                          <Chip label="No" color="success" size="small" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {call.recentAbuse === true ? (
                          <Chip label="Yes" color="error" size="small" />
                        ) : call.recentAbuse === false ? (
                          <Chip label="No" color="success" size="small" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {call.spammer === true ? (
                          <Chip label="Yes" color="error" size="small" />
                        ) : call.spammer === false ? (
                          <Chip label="No" color="success" size="small" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {call.finalAction ? (
                          <Chip
                            label={normalizeAction(call.finalAction)}
                            color={getActionColor(call.finalAction)}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {call.reason || '-'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {call.translationSession ? (
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Chip
                              label={formatTranslationStatus(call.translationSession.status)}
                              color={getTranslationStatusColor(call.translationSession.status)}
                              size="small"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatLanguage(call.translationSession.detectedSourceLanguage)} to {formatLanguage(call.translationSession.targetLanguage)}
                            </Typography>
                            {call.translationSession.skipReason && (
                              <Typography variant="caption" color="warning.main">
                                {formatTranslationStatus(call.translationSession.skipReason)}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {call.callId ? (
                          <Tooltip title="View call trace">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewTrace(call.callId)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                    {canExpand && (
                      <TableRow>
                        <TableCell colSpan={12} sx={{ py: 0, border: isExpanded ? undefined : 'none' }}>
                          <Collapse in={isExpanded} unmountOnExit>
                            <PatternMemoryCard
                              callerId={call.phoneNumber}
                              victimId={call.calleeNumber}
                              onViewDetail={() => navigate(`/risk-meter`)}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={data?.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
    </Box>
  );
}
